import { useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, ContentCard, Row } from "@narmi/design_system";
import { DollarAmountInput } from "cerulean";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { OptionCard, OptionCardGroup } from "@lending/components/OptionCard";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useListApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useListApplicationApplicantDetailsQuery";
import { usePutApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/usePutApplicationApplicantDetailsQuery";
import { useLendingApplicationSteps } from "@lending/hooks/useLendingApplicationSteps";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import { InfoAlert } from "@lending/pages/Common/components/InfoAlert";
import { findPrimaryApplicant } from "@lending/utils/applicantHelpers";

type HousingStatus = "rent" | "own";

function createSchema(l10n: ReturnType<typeof useLocalization>["l10n"]) {
  const requiredMessage = l10n.getString("error-required", null, "Required");

  return z.object({
    monthly_housing_amount: z
      .union([
        z.string().transform((val) => {
          if (val === "$" || val === "") return null;
          const numericValue = parseFloat(val.replace(/[$,]/g, ""));
          return isNaN(numericValue) ? null : numericValue;
        }),
        z.number(),
      ])
      .refine((val): val is number => val !== null && val >= 0, { message: requiredMessage }),
  });
}

type FormData = z.infer<ReturnType<typeof createSchema>>;

function LivingArrangement() {
  const { goNext, startOver, goBack } = useFlowNavigation();
  const { l10n } = useLocalization();
  const schema = createSchema(l10n);
  const { showGenericErrorToast } = useToastsContext();
  const { showLivingArrangementStep } = useLendingApplicationSteps();

  const { data } = useListApplicationApplicantDetailsQuery();
  const applicant = findPrimaryApplicant(data?.results);

  const [selectedStatus, setSelectedStatus] = useState<HousingStatus | null>(
    (applicant?.housing_status as HousingStatus) ?? null
  );

  const defaultAmount = applicant?.monthly_housing_amount_cents
    ? applicant.monthly_housing_amount_cents / 100
    : undefined;

  const { mutateAsync: updateApplicantDetails, isPending } = usePutApplicationApplicantDetailsQuery(
    {
      onError: () => {
        showGenericErrorToast();
      },
    }
  );

  const { handleSubmit, control } = useForm<FormData>({
    defaultValues: {
      monthly_housing_amount: defaultAmount,
    },
    resolver: zodResolver(schema),
  });

  async function onSubmit(formData: FormData) {
    if (!applicant || !selectedStatus) {
      showGenericErrorToast();
      startOver();
      return;
    }

    await updateApplicantDetails({
      params: { path: { uuid: applicant.id } },
      body: {
        ...applicant,
        housing_status: selectedStatus,
        monthly_housing_amount_cents: Math.round(formData.monthly_housing_amount * 100),
      },
    });

    goNext();
  }

  useEffect(() => {
    if (!showLivingArrangementStep) {
      goNext();
    }
  }, [goNext, showLivingArrangementStep]);

  if (!showLivingArrangementStep) {
    return null;
  }

  const isOwner = selectedStatus === "own";

  return (
    <PageLayout>
      <PageContent>
        <PageHeader
          heading={l10n.getString(
            "living-arrangement-heading",
            null,
            "What's your living arrangement?"
          )}
          subheading=""
        />
        <OptionCardGroup>
          <OptionCard
            icon={<span className="narmi-icon-home fontSize--l" />}
            label={l10n.getString("living-arrangement-own", null, "I own my home")}
            isSelected={selectedStatus === "own"}
            onClick={() => setSelectedStatus("own")}
          />
          <OptionCard
            icon={<span className="narmi-icon-key fontSize--l" />}
            label={l10n.getString("living-arrangement-rent", null, "I am renting")}
            isSelected={selectedStatus === "rent"}
            onClick={() => setSelectedStatus("rent")}
          />
        </OptionCardGroup>
        {selectedStatus && (
          <>
            <div className="margin--top--xl">
              <ContentCard kind="bordered" radiusSize="m">
                <div className="fontWeight--bold fontSize--m margin--bottom--xs">
                  {isOwner
                    ? l10n.getString(
                        "housing-payment-heading-mortgage",
                        null,
                        "Monthly mortgage payment"
                      )
                    : l10n.getString("housing-payment-heading-rent", null, "Monthly rent payment")}
                </div>
                <div className="fontSize--s fontColor--secondary margin--bottom--m">
                  {isOwner
                    ? l10n.getString(
                        "housing-payment-subheading-mortgage",
                        null,
                        "Tell us how much you pay towards housing each month. Include all expenses such as mortgage payment, HOA, and insurance."
                      )
                    : l10n.getString(
                        "housing-payment-subheading-rent",
                        null,
                        "Tell us how much you pay towards housing each month."
                      )}
                </div>
                <DollarAmountInput
                  name="monthly_housing_amount"
                  control={control}
                  label={
                    isOwner
                      ? l10n.getString(
                          "housing-payment-label-mortgage",
                          null,
                          "Monthly mortgage payment"
                        )
                      : l10n.getString("housing-payment-label-rent", null, "Monthly rent payment")
                  }
                />
                {!isOwner && (
                  <div className="margin--top--m">
                    <InfoAlert
                      message={l10n.getString(
                        "housing-payment-info-rent",
                        null,
                        "If you share your payment with someone, only include your share."
                      )}
                    />
                  </div>
                )}
              </ContentCard>
            </div>
            <div className="margin--top--xxl">
              <Row alignItems="center" justifyContent="space-between">
                <BackButton onClick={goBack} />
                <Button
                  label={l10n.getString("button-label-next", null, "Next")}
                  onClick={handleSubmit(onSubmit)}
                  isLoading={isPending}
                />
              </Row>
            </div>
          </>
        )}
        {!selectedStatus && (
          <div className="margin--top--xxl">
            <Row alignItems="center" justifyContent="space-between">
              <BackButton onClick={goBack} />
            </Row>
          </div>
        )}
      </PageContent>
    </PageLayout>
  );
}

export { LivingArrangement };
